import { useState, useCallback } from 'react'

import { Cookies } from '@saatva-bits/pattern-library.utils.cookies'
import { open as openChat, useChatZindex } from '@saatva-bits/pattern-library.utils.chat'
import { useExperiment } from '@saatva-bits/pattern-library.modules.launch-darkly'
import { MastheadSiteNav } from '@saatva-bits/pattern-library.modules.navigation'
import { SearchBar } from '@saatva-bits/pattern-library.modules.search'
import { UserAccountPopup } from '@saatva-bits/pattern-library.components.user-account-popup'

import { pushSearchViewed } from '@/utils/analytics'
import { saatvaContactInfo } from '@/config/globals'

const IMGIX_URL = process.env.NEXT_PUBLIC_IMGIX_URL
const ALGOLIA_SEARCH_INDEX = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_INDEX
const ALGOLIA_ARTICLE_INDEX = process.env.NEXT_PUBLIC_ALGOLIA_ARTICLE_INDEX
const SEARCH_INDICES = [ALGOLIA_SEARCH_INDEX, ALGOLIA_ARTICLE_INDEX]

export default function Nav({ navigationData, cartTotalItems, viewingRoomData }) {
    const [searchOpen, setSearchOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const { isV1: isDesktopExpandedRedesignTest } = useExperiment('EXP.DT_EXPANDED_NAV_NEW.DIS-139')
    const { isV0: hideFavorites } = useExperiment('EXP.FAVORITES.DIS-530')
    const { value: navAssetsVersion } = useExperiment('EXP.MATTRESS_IMAGE_NAV.DIS-562')
    const { isV1: isLastUpdatedTest } = useExperiment('EXP.LAST_UPDATED_TIME.CONV-407')

    const toggleSearchBar = () => setSearchOpen((prev) => !prev)

    /* If hideVrIcon is true, icon will be completely hidden, allowing the LD flag to override the display completely */
    const hideVrIcon = viewingRoomData.hideVrIcon
    const viewingRoomUrl = viewingRoomData.url

    useChatZindex([menuOpen, searchOpen])

    const searchAnalyticsCallback = useCallback(() => {
        const gaCookieValue = Cookies.get('_ga')
        // Removes prefix and converts periods to underscores
        const gaUserIdFromCookie = gaCookieValue?.split('.')?.slice(-2)?.join('_')
        pushSearchViewed(gaUserIdFromCookie)
    }, [])

    const searchProps = {
        isSearchOpen: searchOpen,
        isMenuOpen: menuOpen,
        onClose: toggleSearchBar,
        searchIndices: SEARCH_INDICES,
        analyticsCallback: searchAnalyticsCallback,
        serviceOptions: {
            SAATVA_DOMAIN: process.env.NEXT_PUBLIC_SAATVA_DOMAIN,
            API_ROUTES: {
                content: process.env.NEXT_PUBLIC_CONTENT_URL,
                catalog: process.env.NEXT_PUBLIC_CATALOG_URL
            },
            IMGIX_URL: process.env.NEXT_PUBLIC_IMGIX_URL
        }
    }

    const userAccountPopup = <UserAccountPopup saatvaDomain={process.env.NEXT_PUBLIC_SAATVA_DOMAIN} />

    // Emulate cartContext count as it is the unique value needed to render the cart icon count
    // ommiting to pass cartFlyout actions to avoid weird behavior on hover over contact us icon
    const cartContextCount = { cartCount: cartTotalItems }

    const navProps = {
        viewingRoomUrl: viewingRoomUrl,
        hideVrIcon: hideVrIcon,
        handleChat: openChat,
        topLevelItems: navigationData,
        imgixDomain: IMGIX_URL,
        onClickSearchButton: toggleSearchBar,
        onMenuToggle: setMenuOpen,
        hideOrderTracker: false,
        hideCart: false,
        hideCartFlyout: true,
        hideFavorites: hideFavorites,
        cartContext: cartContextCount,
        userAccountPopup,
        contactPhoneNumber: isLastUpdatedTest ? saatvaContactInfo.phoneNumberV1 : saatvaContactInfo.phoneNumberV0,
        isDesktopExpandedRedesignTest,
        navAssetsVersion
    }

    return (
        <header className={'headerClasses'} id="sticky-header">
            <div>
                {/* TODO: Cross-application updates coming to account for:
                    - further consolidated logic for implementing the Nav (PD-15115)
                    - leveraging Link component for same-app routing when possible (if worth the squeeze)
                */}
                <MastheadSiteNav {...navProps}>
                    <SearchBar {...searchProps} />
                </MastheadSiteNav>
            </div>
        </header>
    )
}
